import { useTranslation } from 'react-i18next'
import { WizardState, Exam, EXAM_COLORS, DIFFICULTY_MINUTES, Difficulty } from '../types/index'
import { generateId, formatMinutes } from '../lib/utils'

interface Props {
  state: WizardState
  updateState: (updates: Partial<WizardState>) => void
}

export function Step4({ state, updateState }: Props) {
  const { t } = useTranslation()

  const getExams = (): Exam[] => {
    const exams = [...state.exams]
    while (exams.length < state.examCount) {
      exams.push({
        id: generateId(),
        name: '',
        examDate: new Date(),
        totalPages: 0,
        difficulty: 'medium',
        revisionDays: 0,
        color: EXAM_COLORS[exams.length % EXAM_COLORS.length],
      })
    }
    return exams.slice(0, state.examCount)
  }

  const exams = getExams()

  const updateExam = (index: number, updates: Partial<Exam>) => {
    const updated = [...exams]
    updated[index] = { ...updated[index], ...updates }
    updateState({ exams: updated })
  }

  const getEstimatedTime = (exam: Exam) => {
    const mpp = exam.difficulty === 'custom' ? (exam.customMinutesPerPage || 0) : DIFFICULTY_MINUTES[exam.difficulty]
    const mins = mpp * exam.totalPages
    return mins > 0 ? formatMinutes(mins) : null
  }

  const difficulties: Difficulty[] = ['easy', 'medium', 'hard', 'very_hard', 'custom']
  const diffMins: Record<string, string> = { easy: '3 min', medium: '5 min', hard: '8 min', very_hard: '12 min', custom: '' }

  const inputStyle = {
    width: '100%', border: '1px solid #E5E7EB', borderRadius: 12,
    padding: '10px 16px', fontSize: 15, color: '#111827',
    outline: 'none', boxSizing: 'border-box' as const, display: 'block'
  }
  const dateInputStyle = {
    width: '100%', maxWidth: '100%', border: '1px solid #E5E7EB', borderRadius: 12,
    padding: '10px 16px', fontSize: 15, color: '#111827',
    outline: 'none', boxSizing: 'border-box' as const, display: 'block',
    overflow: 'hidden' as const
  }

  return (
    <div style={{ width: '100%', maxWidth: 480 }}>
      {exams.map((exam, index) => (
        <div key={exam.id} style={{
          backgroundColor: 'white', borderRadius: 20, border: '2px solid ' + exam.color + '40',
          padding: 24, marginBottom: 24
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: exam.color }} />
            <span style={{ fontWeight: 'bold', color: '#374151', fontSize: 13 }}>
              {t('step4.examLabel')} {index + 1}
            </span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>{t('step4.subjectName')}</label>
            <input type="text" value={exam.name} onChange={e => updateExam(index, { name: e.target.value })}
              placeholder={t('step4.subjectPlaceholder')} style={inputStyle} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>{t('step4.examDate')}</label>
            <div style={{ overflow: 'hidden', borderRadius: 12, border: '1px solid #E5E7EB' }}>
              <input type="date"
                value={exam.examDate instanceof Date && !isNaN(exam.examDate.getTime()) ? exam.examDate.toISOString().split('T')[0] : ''}
                onChange={e => updateExam(index, { examDate: new Date(e.target.value) })}
                style={{ width: '100%', border: 'none', borderRadius: 12, padding: '10px 16px', fontSize: 15, color: '#111827', outline: 'none', boxSizing: 'border-box' as const, display: 'block' }} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>{t('step4.totalPages')}</label>
            <input type="number" min="0" value={exam.totalPages || ''}
              onChange={e => updateExam(index, { totalPages: parseInt(e.target.value) || 0 })} style={inputStyle} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 8 }}>{t('step4.difficulty')}</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {difficulties.map(d => (
                <button key={d} onClick={() => updateExam(index, { difficulty: d })}
                  style={{
                    flex: 1, minWidth: 70, padding: '8px 4px', borderRadius: 12, fontSize: 12,
                    fontWeight: 500, cursor: 'pointer', border: 'none',
                    backgroundColor: exam.difficulty === d ? exam.color : '#F9FAFB',
                    color: exam.difficulty === d ? 'white' : '#4B5563',
                    transition: 'all 0.2s'
                  }}>
                  <div style={{ fontWeight: 'bold' }}>{t('step4.' + (d === 'very_hard' ? 'veryHard' : d))}</div>
                  {d !== 'custom' && <div style={{ opacity: 0.75, fontSize: 11 }}>{diffMins[d]}</div>}
                  {d === 'custom' && exam.difficulty === 'custom' && (
                    <input type="number" min="1" max="60" value={exam.customMinutesPerPage || ''}
                      onClick={e => e.stopPropagation()}
                      onChange={e => updateExam(index, { customMinutesPerPage: parseInt(e.target.value) || 0 })}
                      placeholder="?" style={{
                        width: '100%', marginTop: 4, backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white', textAlign: 'center', border: 'none', borderRadius: 4,
                        padding: '2px 4px', fontSize: 11, outline: 'none'
                      }} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {getEstimatedTime(exam) && (
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
              {t('step4.estimatedTime')}: <strong style={{ color: '#111827' }}>{getEstimatedTime(exam)}</strong>
            </p>
          )}

          <div>
            <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>
              {t('step4.revisionDays')} <span style={{ color: '#9CA3AF' }}>{t('step4.revisionOptional')}</span>
            </label>
            <input type="number" min="0" value={exam.revisionDays || ''}
              onChange={e => updateExam(index, { revisionDays: parseInt(e.target.value) || 0 })} style={inputStyle} />
          </div>
        </div>
      ))}
    </div>
  )
}
